/**
 * Admin v2 shell — desktop-first dashboard for the new admin.
 *
 * Lives at /admin/v2 (and /admin/v2/<screen>). Both the old /admin and this
 * new dashboard coexist behind the same is_admin gate — a link on each side
 * flips between them. Handing the swap off later is a one-line router.js
 * change (repoint /admin at renderAdminV2Shell).
 *
 * The shell owns:
 *   - the sidebar navigation (screen-key routing without a full page reload
 *     when clicking a sidebar item; deep links still hit the router directly),
 *   - the topbar (title, subtitle, controls, range pill),
 *   - persistent range state (date + granularity) via sessionStorage,
 *   - shared theme toggle (dark|light).
 *
 * Each sub-screen exports `renderXxx(host, ctx)` where ctx carries the
 * current range state + a `refresh()` callback for CSV/reload after
 * mutations. Sub-screens only mount their content region — the shell
 * manages the layout chrome.
 */

import { auth } from '../../auth.js';
import { escape } from '../../components/form.js';
import { navigate } from '../../router.js';
import {
  applyPreset,
  compareLabel,
  loadRange,
  PRESET_LABELS,
  rangeLabel,
  saveRange,
} from './common.js';
import { renderAntiCheat } from './anti_cheat.js';
import { renderCampaigns } from './campaigns.js';
import { renderDuelDetail } from './duel_detail.js';
import { exportCsv as exportDuelsCsv, renderDuels } from './duels.js';
import { exportCsv as exportFinancialCsv, renderFinancial } from './financial.js';
import { exportCsv as exportFriendsCsv, renderFriends } from './friends.js';
import { renderGame } from './game.js';
import { renderLeaderboardRewards } from './leaderboard_rewards.js';
import { renderLive } from './live.js';
import { renderModeration } from './moderation.js';
import { renderOverview } from './overview.js';
import { renderPlayerDetail } from './player_detail.js';
import { renderPlayers } from './players.js';
import { exportCsv as exportPowerupsCsv, renderPowerups } from './powerups.js';
import { renderReferrerDetail } from './referrer_detail.js';
import { renderRetestDetail } from './retest_detail.js';
import { exportCsv as exportRevenueCsv, renderRevenue } from './revenue.js';
import { renderSpec } from './spec.js';

// Screen-key → exportCsv fn. Screens without a CSV export are absent from
// the map so we can't accidentally paint the button on screens that can't
// serve one — pairs with the `csv: true` flag on SCREENS[].
const CSV_EXPORTERS = {
  revenue: exportRevenueCsv,
  financial: exportFinancialCsv,
  duels: exportDuelsCsv,
  friends: exportFriendsCsv,
  powerups: exportPowerupsCsv,
};

const THEME_KEY = 'adm2-theme';
const THEMES = ['dark', 'light'];

const SCREENS = [
  {
    group: 'Money',
    items: [
      { key: 'overview', label: 'Overview', title: 'Overview', subtitle: 'Money, players, games & content health at a glance' },
      { key: 'revenue', label: 'Revenue & Earnings', title: 'Revenue & Earnings', subtitle: 'Handle, rake, prizes and net revenue over the selected window', csv: true },
      { key: 'financial', label: 'Financial & Ops', title: 'Financial & Operations', subtitle: 'Balances, provider flows, payouts and ledger reconciliation', csv: true },
    ],
  },
  {
    group: 'Players',
    items: [
      { key: 'players', label: 'Players', title: 'Players', subtitle: 'Audience totals, activity and the full user directory' },
      { key: 'duels', label: 'Duels', title: 'Duels (1v1)', subtitle: 'Lifecycle, stakes, matchmaking and rate health', csv: true },
      { key: 'friends', label: 'Friends & Referrals', title: 'Friends & Referrals', subtitle: 'Referral conversion and friendship growth', csv: true },
    ],
  },
  {
    group: 'Content',
    items: [
      { key: 'game', label: 'Game Health', title: 'Game Health & Content', subtitle: 'Live funnels, question quality and content fatigue' },
      { key: 'powerups', label: 'Power-Ups', title: 'Power-Ups', subtitle: 'Eraser & 50/50 sales, usage, grants and liability', csv: true },
    ],
  },
  {
    group: 'Live',
    items: [
      { key: 'live', label: 'Live Events', title: 'Live Trivia Events', subtitle: 'Per-event funnel, payouts and house economics' },
    ],
  },
  {
    group: 'Marketing',
    items: [
      { key: 'campaigns', label: 'Email Campaigns', title: 'Email Campaigns', subtitle: 'Create HTML emails, target dormant users, preview and send' },
    ],
  },
  {
    group: 'System',
    items: [
      { key: 'anti_cheat', label: 'Anti-cheat', title: 'Anti-cheat', subtitle: 'Suspicion score weights + payout penalty curve' },
      { key: 'moderation', label: 'Moderation', title: 'Moderation', subtitle: 'Per-correct-answer penalty applied to banned players' },
      { key: 'leaderboard_rewards', label: 'Leaderboard rewards', title: 'Weekly leaderboard rewards', subtitle: 'Auto-credit the top-N weekly players every Sunday midnight WAT' },
      { key: 'spec', label: 'Patterns & Spec', title: 'Patterns & Spec', subtitle: 'Reusable cards, charts, tables and control conventions' },
    ],
  },
];

const SCREEN_MAP = new Map(SCREENS.flatMap((g) => g.items.map((it) => [it.key, it])));

const RENDERERS = {
  overview: renderOverview,
  revenue: renderRevenue,
  financial: renderFinancial,
  players: renderPlayers,
  duels: renderDuels,
  friends: renderFriends,
  referrer_detail: renderReferrerDetail,
  game: renderGame,
  powerups: renderPowerups,
  live: renderLive,
  campaigns: renderCampaigns,
  anti_cheat: renderAntiCheat,
  moderation: renderModeration,
  leaderboard_rewards: renderLeaderboardRewards,
  spec: renderSpec,
};

// ---------------------------------------------------------------------------
// Screen resolution — parse the URL, decide which renderer and any params.
// ---------------------------------------------------------------------------

function resolveScreen(path) {
  // Strip /admin/v2 prefix and any trailing slash.
  const rest = path.replace(/^\/admin\/v2/, '').replace(/^\/+|\/+$/g, '');
  if (!rest) return { key: 'overview' };
  const [head, tail] = rest.split('/');
  // Player detail: /admin/v2/players/:id → dispatch to player_detail with the id.
  if (head === 'players' && tail) return { key: 'player_detail', playerId: tail };
  // Live per-event: /admin/v2/live/:id → live screen still handles it (it
  // renders the drill-down inline).
  if (head === 'live' && tail) return { key: 'live', liveId: tail };
  // Duel detail: /admin/v2/duels/:id → per-duel client-integrity page.
  if (head === 'duels' && tail) return { key: 'duel_detail', duelId: tail };
  // Retest detail: /admin/v2/retests/:id → deep-link target for the
  // "defend your score" reviewer email. `?player=<uid>` in the query
  // string threads the player id so the "back" link lands on the
  // correct player detail page.
  if (head === 'retests' && tail) {
    const params = new URLSearchParams(window.location.search);
    return { key: 'retest_detail', retestId: tail, playerId: params.get('player') || null };
  }
  // Campaigns composer: /admin/v2/campaigns/:id → same screen, form mode.
  if (head === 'campaigns' && tail) return { key: 'campaigns', campaignId: tail };
  // Referrer detail: /admin/v2/friends/:user_id → drill-down showing
  // who this user has referred.
  if (head === 'friends' && tail) return { key: 'referrer_detail', referrerId: tail };
  if (SCREEN_MAP.has(head)) return { key: head };
  return { key: 'overview' };
}

// ---------------------------------------------------------------------------
// Shell render
// ---------------------------------------------------------------------------

export async function renderAdminV2Shell(root) {
  const me = auth.getMe();
  if (!me?.is_admin) {
    root.innerHTML = `
      <div style="padding:40px;color:#F87171;font:600 15px 'IBM Plex Sans',sans-serif;background:#0A1120;min-height:100vh;">
        Admins only.
      </div>
    `;
    return;
  }

  // Screen resolution — supports both /admin/v2 root and deep sub-paths.
  const resolved = resolveScreen(window.location.pathname);
  let meta;
  if (resolved.key === 'player_detail') {
    meta = { key: 'players', title: 'Player detail', subtitle: 'Wallet, inventory, duel record and live-trivia honours' };
  } else if (resolved.key === 'referrer_detail') {
    meta = { key: 'friends', title: 'Referrer detail', subtitle: 'Everyone this user has referred' };
  } else if (resolved.key === 'duel_detail') {
    meta = { key: 'duels', title: 'Duel detail', subtitle: 'Per-run client-integrity signals' };
  } else if (resolved.key === 'retest_detail') {
    meta = { key: 'players', title: 'Re-test review', subtitle: 'Grade + client-integrity signals · verdict is advisory' };
  } else {
    meta = SCREEN_MAP.get(resolved.key) || SCREEN_MAP.get('overview');
  }

  // Theme + range state, both persisted in sessionStorage.
  let theme = _loadTheme();
  let range = loadRange();

  const renderShell = () => {
    root.innerHTML = `
      <div class="adm2 ${theme === 'light' ? 'light' : ''}">
        <aside class="adm2-side">${_renderSidebar(_sidebarKey(resolved.key))}</aside>
        <main class="adm2-main">
          <header class="adm2-top">${_renderTopbar(meta, range, theme)}</header>
          <section class="adm2-content" id="adm2-content">
            <div class="adm2-skl" style="height:220px;width:100%;border-radius:14px;"></div>
          </section>
        </main>
      </div>
    `;
    _wireSidebar(root);
    _wireTopbar(root, {
      range,
      onRangeChange: (next) => {
        range = next;
        saveRange(range);
        // Re-render the topbar so the active preset highlight moves + the
        // range pill updates to the new label; then re-mount the screen.
        renderShell();
        mountScreen();
      },
      theme,
      onThemeToggle: () => {
        theme = theme === 'dark' ? 'light' : 'dark';
        _saveTheme(theme);
        renderShell();
        mountScreen();
      },
      csv: meta.csv,
      screenKey: resolved.key,
    });
  };

  const mountScreen = () => {
    const host = root.querySelector('#adm2-content');
    if (!host) return;
    const ctx = {
      range,
      setRange: (next) => {
        range = { ...range, ...next };
        saveRange(range);
        renderShell();
        mountScreen();
      },
      navigate: (to) => navigate(to),
      playerId: resolved.playerId,
      liveId: resolved.liveId,
      campaignId: resolved.campaignId,
      referrerId: resolved.referrerId,
      duelId: resolved.duelId,
      retestId: resolved.retestId,
    };
    if (resolved.key === 'player_detail') {
      renderPlayerDetail(host, ctx);
      return;
    }
    if (resolved.key === 'duel_detail') {
      renderDuelDetail(host, ctx);
      return;
    }
    if (resolved.key === 'retest_detail') {
      renderRetestDetail(host, ctx);
      return;
    }
    const renderer = RENDERERS[resolved.key];
    if (renderer) {
      renderer(host, ctx);
    } else {
      host.innerHTML = '<div class="adm2-error">Unknown screen.</div>';
    }
  };

  renderShell();
  mountScreen();
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

// Which SCREENS entry a given `resolved.key` should light up in the
// sidebar. Detail views don't have their own sidebar row — they share
// with the parent screen they belong to.
function _sidebarKey(key) {
  if (key === 'player_detail') return 'players';
  if (key === 'referrer_detail') return 'friends';
  if (key === 'duel_detail') return 'duels';
  if (key === 'retest_detail') return 'players';
  return key;
}

function _renderSidebar(activeKey) {
  const groups = SCREENS.map(
    (g) => `
    <div>
      <div class="adm2-side-group-label">${escape(g.group)}</div>
      ${g.items
        .map(
          (it) => `
        <a href="/admin/v2/${escape(it.key)}" data-link
           class="adm2-side-item ${it.key === activeKey ? 'active' : ''}">
          <span class="adm2-side-dot"></span>
          <span style="flex:1;">${escape(it.label)}</span>
        </a>
      `,
        )
        .join('')}
    </div>
  `,
  ).join('');
  return `
    <div class="adm2-side-brand">
      <div class="adm2-side-brand-mark">T</div>
      <div style="line-height:1.1;">
        <div class="adm2-side-brand-name">Triviana</div>
        <div class="adm2-side-brand-sub">admin · v2 preview</div>
      </div>
    </div>
    <nav class="adm2-side-nav">${groups}</nav>
    <div class="adm2-side-foot">
      <a href="/admin" data-link class="adm2-side-back">‹ Old admin dashboard</a>
      <a href="/" data-link class="adm2-side-back">‹ Back to app</a>
    </div>
  `;
}

function _wireSidebar(root) {
  // Sidebar links use data-link so router.js handles navigation. Nothing to
  // wire here; the router listener picks them up.
  void root;
}

// ---------------------------------------------------------------------------
// Topbar
// ---------------------------------------------------------------------------

function _renderTopbar(meta, range, theme) {
  const gran = (opts, active) =>
    opts
      .map(
        (o) => `
      <button type="button" data-gran="${escape(o.key)}" class="${active === o.key ? 'active' : ''}">${escape(o.label)}</button>
    `,
      )
      .join('');
  const themeIcon = theme === 'dark' ? '☀' : '☾';
  const themeLabel = theme === 'dark' ? 'Light' : 'Dark';
  return `
    <div class="adm2-top-row">
      <div style="min-width:240px;">
        <div class="adm2-top-title">${escape(meta.title)}</div>
        <div class="adm2-top-sub">${escape(meta.subtitle)}</div>
      </div>
      <div class="adm2-controls">
        <button type="button" class="adm2-btn" id="adm2-theme-toggle" title="Toggle theme">
          <span style="font-size:13px;">${escape(themeIcon)}</span> ${escape(themeLabel)}
        </button>
        ${_renderPresetDropdown(range)}
        <div class="adm2-seg">
          ${gran([
            { key: 'day', label: 'Day' },
            { key: 'week', label: 'Week' },
          ], range.gran)}
        </div>
        ${meta.csv ? `<button type="button" class="adm2-btn primary" id="adm2-csv-btn">↓ Export CSV</button>` : ''}
      </div>
    </div>
    ${range.preset === 'custom' ? _renderCustomPicker(range) : ''}
    <div class="adm2-context">
      <span class="adm2-pill"><span style="color:#A78BFA;">▷</span> ${escape(rangeLabel(range))}</span>
      <span style="font:400 12px 'IBM Plex Sans',sans-serif;color:var(--a-muted);">${escape(compareLabel(range))}</span>
      <span style="display:inline-flex;align-items:center;gap:7px;font:500 11px 'IBM Plex Sans',sans-serif;color:var(--a-muted);margin-left:auto;">
        <span style="width:8px;height:8px;border-radius:2px;background:var(--a-panel);border:1px solid var(--a-border);"></span>Windowed
        <span style="width:8px;height:8px;border-radius:2px;background:rgba(233,199,126,.14);border:1px solid var(--a-warm-bd);margin-left:8px;"></span>Live snapshot
      </span>
    </div>
  `;
}

// Preset order matches the screenshot spec:
// Today, All Time, Custom Period, This Week, This Month, This Year, Last 30 days, Last 90 days.
// `custom` is a mode toggle rather than a range — clicking it flips into
// the inline start/end picker below; nothing commits until Apply.
const _PRESET_ORDER = [
  'today',
  'all',
  'custom',
  'this_week',
  'this_month',
  'this_year',
  '30d',
  '90d',
];

function _renderPresetDropdown(range) {
  const currentLabel = PRESET_LABELS[range.preset] || 'Range';
  const items = _PRESET_ORDER
    .map((k) => {
      const active = k === range.preset;
      return `
        <button type="button" role="menuitem"
                data-range-preset="${escape(k)}"
                aria-selected="${active ? 'true' : 'false'}"
                class="adm2-preset-item ${active ? 'active' : ''}">
          <span>${escape(PRESET_LABELS[k])}</span>
          ${active ? '<span aria-hidden="true">✓</span>' : ''}
        </button>
      `;
    })
    .join('');
  return `
    <div class="adm2-preset-wrap" data-open="false">
      <button type="button" class="adm2-btn adm2-preset-trigger"
              aria-haspopup="menu" aria-expanded="false"
              id="adm2-preset-trigger">
        <span>${escape(currentLabel)}</span>
        <span aria-hidden="true" style="opacity:.7;">▾</span>
      </button>
      <div class="adm2-preset-menu" role="menu" aria-labelledby="adm2-preset-trigger">
        ${items}
      </div>
    </div>
  `;
}

function _renderCustomPicker(range) {
  // Inline strip beneath the topbar with two date inputs + Apply. The
  // shell re-renders on every range change so the picker is 'reset' each
  // time — that's fine because Apply is a single-shot commit.
  const today = _todayIso();
  return `
    <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:12px;padding:11px 14px;
                background:var(--a-panel);border:1px solid var(--a-border);border-radius:10px;">
      <span style="font:600 11px 'IBM Plex Sans',sans-serif;letter-spacing:.05em;text-transform:uppercase;color:var(--a-muted);">Custom range</span>
      <label style="display:inline-flex;align-items:center;gap:6px;font:500 12px 'IBM Plex Sans',sans-serif;color:var(--a-ink-2);">
        Start
        <input type="date" id="adm2-range-start" value="${escape(range.start || '')}" max="${escape(today)}" class="adm2-input" style="padding:6px 10px;font-size:12.5px;" />
      </label>
      <label style="display:inline-flex;align-items:center;gap:6px;font:500 12px 'IBM Plex Sans',sans-serif;color:var(--a-ink-2);">
        End
        <input type="date" id="adm2-range-end" value="${escape(range.end || '')}" max="${escape(today)}" class="adm2-input" style="padding:6px 10px;font-size:12.5px;" />
      </label>
      <button type="button" id="adm2-range-apply" class="adm2-btn primary" style="padding:6px 14px;">Apply</button>
      <span id="adm2-range-err" style="font:500 12px 'IBM Plex Sans',sans-serif;color:var(--a-bad);"></span>
    </div>
  `;
}

// Lagos-local "today" (UTC+1) as YYYY-MM-DD. Used to cap the date inputs so
// nobody picks a future end date the backend will reject.
function _todayIso() {
  const lagos = new Date(Date.now() + 60 * 60 * 1000);
  return lagos.toISOString().slice(0, 10);
}

function _wireTopbar(root, { range, onRangeChange, onThemeToggle, csv, screenKey }) {
  root.querySelectorAll('[data-range-preset]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const preset = btn.dataset.rangePreset;
      // Clicking Custom just switches into the picker mode — it doesn't
      // commit a new range. The Apply button below commits.
      if (preset === 'custom') {
        onRangeChange({ ...range, preset: 'custom' });
        return;
      }
      onRangeChange(applyPreset(range, preset));
    });
  });
  root.querySelectorAll('[data-gran]').forEach((btn) => {
    btn.addEventListener('click', () => {
      onRangeChange({ ...range, gran: btn.dataset.gran });
    });
  });
  // Preset dropdown open/close + outside-click dismiss. `data-open` on
  // the wrap element is the styling hook — see admin_v2.css.
  const presetWrap = root.querySelector('.adm2-preset-wrap');
  const presetTrigger = root.querySelector('#adm2-preset-trigger');
  if (presetWrap && presetTrigger) {
    const close = () => {
      presetWrap.dataset.open = 'false';
      presetTrigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', outside, true);
      document.removeEventListener('keydown', onKey, true);
    };
    const open = () => {
      presetWrap.dataset.open = 'true';
      presetTrigger.setAttribute('aria-expanded', 'true');
      // Deferred so THIS click doesn't immediately close via outside-click.
      setTimeout(() => {
        document.addEventListener('click', outside, true);
        document.addEventListener('keydown', onKey, true);
      }, 0);
    };
    const outside = (e) => {
      if (!presetWrap.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    presetTrigger.addEventListener('click', () => {
      if (presetWrap.dataset.open === 'true') close();
      else open();
    });
    // Item click closes the menu AFTER firing the range change — the
    // shell will re-render this same topbar so we don't need to manually
    // reset the menu state.
    presetWrap.querySelectorAll('.adm2-preset-item').forEach((btn) => {
      btn.addEventListener('click', () => close());
    });
  }
  const applyBtn = root.querySelector('#adm2-range-apply');
  if (applyBtn) {
    applyBtn.addEventListener('click', () => {
      const startEl = root.querySelector('#adm2-range-start');
      const endEl = root.querySelector('#adm2-range-end');
      const errEl = root.querySelector('#adm2-range-err');
      const start = startEl?.value;
      const end = endEl?.value;
      const ok = start && end && start <= end;
      if (!ok) {
        if (errEl) errEl.textContent = 'Pick a valid start ≤ end date.';
        return;
      }
      onRangeChange({ ...range, preset: 'custom', start, end });
    });
  }
  const themeBtn = root.querySelector('#adm2-theme-toggle');
  if (themeBtn) themeBtn.addEventListener('click', onThemeToggle);
  const csvBtn = root.querySelector('#adm2-csv-btn');
  if (csvBtn && csv) {
    csvBtn.addEventListener('click', () => {
      // Delegate to the screen module — each one knows its own CSV path.
      const fn = CSV_EXPORTERS[screenKey];
      if (typeof fn === 'function') fn({ range });
    });
  }
}

// ---------------------------------------------------------------------------
// Theme persistence
// ---------------------------------------------------------------------------

function _loadTheme() {
  try {
    const t = sessionStorage.getItem(THEME_KEY);
    if (THEMES.includes(t)) return t;
  } catch {
    /* private mode */
  }
  return 'dark';
}
function _saveTheme(t) {
  try {
    sessionStorage.setItem(THEME_KEY, t);
  } catch {
    /* private mode */
  }
}
